import { DashboardTemplate } from '../components/templates/DashboardTemplate.tsx';
import { useReport, useMonthlyReportBooks } from '@partiu/api/dashboard/hooks.ts';
import { useState } from 'react';
import { useModules } from '../contexts/ProductsProvider.tsx';
import { AdaptiveStack } from '@partiu/ui/components/atoms/AdaptiveStack.tsx';
import { BigReportDataCard, ReportDataCard } from '@partiu/ui/components/molecules/ReportDataCard.tsx';
import {
  FaBoxOpen,
  FaChartBar,
  FaChartLine,
  FaDollarSign,
  FaNewspaper,
  FaPeopleGroup,
  FaPerson,
  FaPhotoFilm,
  FaStore,
} from 'react-icons/fa6';
import { toBRL, toReadableNumber } from '@partiu/ui/formatters.ts';
import { Select, VStack } from '@chakra-ui/react';
import { Labeled } from '@partiu/ui/components/molecules/Labeled.tsx';
import { BannerCarousel } from '@partiu/ui/components/molecules/Carousel.tsx';
import { useTheme } from '@partiu/ui/contexts/ThemeProvider.tsx';
import { TitledBox } from '@partiu/ui/components/molecules/TitledBox.tsx';
import { ActivationUsageGraph } from '../components/graphs/ActivationUsageGraph.tsx';
import { FaNetworkWired } from 'react-icons/fa';
import { WeekdayUsageGraph } from '../components/graphs/WeekdayUsageGraph.tsx';
import { CategoryUsageGraph } from '../components/graphs/CategoryUsageGraph.tsx';
import { UsageByStoreGraph } from '../components/graphs/UsageByStoreGraph.tsx';
import { MEDIA_MANAGER_HIDDEN_AGREEMENTS } from '../components/templates/RequireModule.tsx';

export function DashboardPage() {
  const { banners, id: agreementId } = useTheme();
  const { hasClub, hasMedia } = useModules();
  const showMedia = hasMedia && !(agreementId && MEDIA_MANAGER_HIDDEN_AGREEMENTS.includes(agreementId));
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const { data: reportData } = useReport(year);
  const { data: monthlyReportData } = useMonthlyReportBooks();

  const currentYear = new Date().getFullYear();
  const years = [];

  for (let year = currentYear; year >= 2021; year--) {
    years.push(year);
  }

  return (
    <DashboardTemplate>
      <BannerCarousel banners={banners} />
      <VStack padding={4} paddingBottom={'50px'} spacing={6} width={'100%'}>
        <Labeled width={'100%'} label={'Ano'}>
          <Select variant="outline" borderColor={'#a5a5a5'} value={year} onChange={(e) => setYear(e.target.value)}>
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </Select>
        </Labeled>

        {showMedia && (
          <AdaptiveStack spacing={5} width={'100%'}>
            <ReportDataCard
              icon={<FaPhotoFilm />}
              formatter={toReadableNumber}
              value={monthlyReportData?.statistics?.totalMedia}
              label={'Total de Mídia'}
            />
            <ReportDataCard
              icon={<FaBoxOpen />}
              formatter={toReadableNumber}
              value={monthlyReportData?.statistics?.totalDelivered}
              label={'Mídias Entregues'}
            />
            <ReportDataCard
              icon={<FaNewspaper />}
              formatter={toReadableNumber}
              value={monthlyReportData?.statistics?.totalMagazines}
              label={'Revistas Entregues'}
            />
          </AdaptiveStack>
        )}

        {hasClub && (
          <>
            <AdaptiveStack spacing={5} width={'100%'}>
              <ReportDataCard
                icon={<FaPeopleGroup />}
                formatter={toReadableNumber}
                value={reportData?.users.all}
                label={'Logins Únicos'}
              />
              <ReportDataCard
                icon={<FaChartBar />}
                formatter={toReadableNumber}
                value={reportData?.usage.all}
                label={'Utilizações'}
              />
              <ReportDataCard
                icon={<FaChartLine />}
                formatter={toReadableNumber}
                value={reportData?.promocode.all}
                label={'Promocodes'}
              />
              <ReportDataCard
                icon={<FaDollarSign />}
                value={reportData?.usage.totalEstimatedSaved}
                label={'Total Economizado'}
                formatter={toBRL}
              />
            </AdaptiveStack>
            <TitledBox display={{ base: 'none', lg: 'flex' }} title={'Usuários x Utilizações'}>
              <ActivationUsageGraph
                userMonthlyCount={reportData?.users?.activatedMonthlyCount}
                usageMonthlyCount={reportData?.usage?.monthlyCount}
              />
            </TitledBox>
            <TitledBox display={{ base: 'flex', lg: 'none' }} title={'Usuários x Utilizações'}>
              <ActivationUsageGraph
                userMonthlyCount={reportData?.users?.activatedMonthlyCount}
                usageMonthlyCount={reportData?.usage?.monthlyCount}
                height={'60px'}
              />
            </TitledBox>
            <AdaptiveStack spacing={5} width={'100%'}>
              <BigReportDataCard
                icon={<FaNetworkWired />}
                formatter={toReadableNumber}
                value={reportData?.usage.online}
                label={'Utilizações E-commerce'}
              />
              <BigReportDataCard
                icon={<FaStore />}
                formatter={toReadableNumber}
                value={reportData?.usage.local}
                label={'Utilizações Físicas'}
              />
              <BigReportDataCard
                icon={<FaPerson />}
                formatter={toReadableNumber}
                value={reportData?.usage.usagePerUser}
                label={'Utilizações Por Usuário'}
              />
            </AdaptiveStack>
            <AdaptiveStack spacing={5} width={'100%'}>
              <TitledBox display={{ base: 'flex', lg: 'none' }} title={'Utilização X Dia da Semana'}>
                <WeekdayUsageGraph weekdayUsage={reportData?.usage.weekDayUsage} height={'120px'} />
              </TitledBox>
              <TitledBox display={{ base: 'none', lg: 'flex' }} title={'Utilização X Dia da Semana'}>
                <WeekdayUsageGraph weekdayUsage={reportData?.usage.weekDayUsage} height={'40px'} />
              </TitledBox>
              <TitledBox display={{ base: 'flex', lg: 'none' }} title={'Uso por Categoria'}>
                <CategoryUsageGraph data={reportData?.usage.categoryUsage} height={'120px'} />
              </TitledBox>
              <TitledBox width={'500px'} display={{ base: 'none', lg: 'flex' }} title={'Uso por Categoria'}>
                <CategoryUsageGraph data={reportData?.usage.categoryUsage} height={'30px'} />
              </TitledBox>
            </AdaptiveStack>
            <AdaptiveStack width={'100%'}>
              <TitledBox display={{ base: 'flex', lg: 'none' }} title={'Ranking de Lojas'}>
                <UsageByStoreGraph highestUsageData={reportData?.usage.highestUsage} height={'240px'} />
              </TitledBox>
              <TitledBox display={{ base: 'none', lg: 'flex' }} title={'Ranking de Lojas'}>
                <UsageByStoreGraph highestUsageData={reportData?.usage.highestUsage} height={'120px'} />
              </TitledBox>
            </AdaptiveStack>
          </>
        )}
      </VStack>
    </DashboardTemplate>
  );
}
